import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import FullscreenOverlay from './FullscreenOverlay'

/**
 * Renders the chart spec the model proposed (server-validated) against the
 * inline result table. Types: bar, line, pie, scatter. null/none → nothing.
 */
/**
 * Highcharts runs in JS and can't read CSS custom properties, so mirror the FD
 * design tokens here. Keep these in sync with src/v2/styles/tailwind.css.
 */
const CHART_TOKENS = {
  primary: '#4D8E97', // teal-600
  navy: '#031C41', // navy-900
  gold: '#C5B358', // gold-500
  teal400: '#87BAC2',
  teal700: '#2F6B73',
  navy400: '#355FA1',
  text: '#0F1422', // text-primary
  textTertiary: '#8893A6',
  border: '#E8ECF2', // border-subtle
}
const TEAL = CHART_TOKENS.primary
const PALETTE = [
  CHART_TOKENS.primary,
  CHART_TOKENS.navy,
  CHART_TOKENS.gold,
  CHART_TOKENS.teal400,
  CHART_TOKENS.navy400,
  CHART_TOKENS.teal700,
]

function colIndex(columns, name) {
  if (name == null) return -1
  return columns.findIndex((c) => String(c).toLowerCase() === String(name).toLowerCase())
}

function toNumber(v) {
  if (typeof v === 'number') return v
  if (v == null) return null
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : null
}

function buildOptions(table, chart) {
  if (!table || !Array.isArray(table.columns) || !Array.isArray(table.rows)) return null
  const { columns, rows } = table
  if (rows.length === 0) return null

  const type = chart.type
  const base = {
    credits: { enabled: false },
    title: chart.title
      ? { text: chart.title, align: 'left', style: { fontFamily: 'Inter', fontSize: '13px', fontWeight: '500', color: CHART_TOKENS.text } }
      : { text: undefined },
    chart: { height: 300, backgroundColor: 'transparent', spacingLeft: 0, spacingRight: 0, style: { fontFamily: 'Inter' } },
    legend: { enabled: false },
    colors: PALETTE,
  }
  const axisLabel = { style: { color: CHART_TOKENS.textTertiary, fontSize: '11px', fontFamily: 'JetBrains Mono' } }

  if (type === 'bar' || type === 'line') {
    const xi = colIndex(columns, chart.x)
    const yi = colIndex(columns, chart.y)
    if (xi < 0 || yi < 0) return null
    return {
      ...base,
      chart: { ...base.chart, type: type === 'bar' ? 'column' : 'line' },
      xAxis: {
        categories: rows.map((r) => String(r[xi])),
        labels: { style: { ...axisLabel.style, fontFamily: 'Inter' } },
        lineColor: CHART_TOKENS.border,
        tickColor: CHART_TOKENS.border,
      },
      yAxis: { title: { text: null }, gridLineColor: CHART_TOKENS.border, labels: axisLabel },
      plotOptions: {
        column: { color: TEAL, borderRadius: 4, borderWidth: 0 },
        line: { color: TEAL, lineWidth: 2, marker: { enabled: false } },
      },
      series: [{ name: String(chart.y), data: rows.map((r) => toNumber(r[yi])) }],
    }
  }

  if (type === 'pie') {
    const li = colIndex(columns, chart.label)
    const vi = colIndex(columns, chart.value)
    if (li < 0 || vi < 0) return null
    return {
      ...base,
      chart: { ...base.chart, type: 'pie' },
      tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
      plotOptions: { pie: { dataLabels: { style: { fontFamily: 'Inter', fontSize: '11px', fontWeight: '400' } } } },
      series: [{ name: String(chart.value), data: rows.map((r) => ({ name: String(r[li]), y: toNumber(r[vi]) })) }],
    }
  }

  if (type === 'scatter') {
    const xi = colIndex(columns, chart.x)
    const yi = colIndex(columns, chart.y)
    if (xi < 0 || yi < 0) return null
    return {
      ...base,
      chart: { ...base.chart, type: 'scatter' },
      xAxis: { title: { text: String(chart.x), style: axisLabel.style }, gridLineColor: CHART_TOKENS.border, labels: axisLabel },
      yAxis: { title: { text: String(chart.y), style: axisLabel.style }, gridLineColor: CHART_TOKENS.border, labels: axisLabel },
      plotOptions: { scatter: { color: TEAL, marker: { radius: 4 } } },
      series: [{ name: `${chart.x} / ${chart.y}`, data: rows.map((r) => [toNumber(r[xi]), toNumber(r[yi])]) }],
    }
  }

  return null
}

export default function ResultChart({ table, chart }) {
  const { t } = useTranslation()
  const options = useMemo(() => {
    if (!chart || !chart.type || chart.type === 'null' || chart.type === 'none') return null
    return buildOptions(table, chart)
  }, [table, chart])

  const [fullscreen, setFullscreen] = useState(false)
  const fsOptions = useMemo(() => {
    if (!options) return null
    const h = typeof window !== 'undefined' ? Math.max(400, window.innerHeight - 150) : 640
    return { ...options, chart: { ...options.chart, height: h } }
  }, [options])

  if (!options) return null

  return (
    <div style={{ marginBottom: '16px' }}>
      <div className="flex items-center justify-end mb-2">
        <button className="fd-btn fd-btn--sm fd-btn--ghost" onClick={() => setFullscreen(true)}>
          <i className="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true" /> {t('Sentinel.V2.table.fullScreen')}
        </button>
      </div>
      <div className="fd-card fd-card--elevated fd-card--p16">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      {fullscreen && (
        <FullscreenOverlay title={chart?.title || t('Sentinel.V2.chart.title')} onClose={() => setFullscreen(false)}>
          <div style={{ padding: '16px' }}>
            <HighchartsReact highcharts={Highcharts} options={fsOptions} />
          </div>
        </FullscreenOverlay>
      )}
    </div>
  )
}

ResultChart.propTypes = { table: PropTypes.object, chart: PropTypes.object }
